import type { ConceptSnapshot, WorkflowRecommendation } from "@/lib/workflowAdvisor";
import { RecommendationList } from "./RecommendationList";

interface Props {
  snapshot: ConceptSnapshot;
  recs: WorkflowRecommendation[];
  onAct: (rec: WorkflowRecommendation) => void;
  /** Open the guide/walkthrough for a recommendation, optionally scrolling to a
   *  section. Optional so the panel still renders (without guide links) when no
   *  handler is wired. */
  onOpenGuide?: (walkthroughId: string, section?: string) => void;
}

type Tone = "done" | "partial" | "todo";
interface Item {
  tone: Tone;
  label: string;
}

const GLYPH: Record<Tone, string> = { done: "✓", partial: "◑", todo: "○" };
const GLYPH_CLASS: Record<Tone, string> = {
  done: "text-primary",
  partial: "text-amber-400",
  todo: "text-muted-foreground/50",
};
const LABEL_CLASS: Record<Tone, string> = {
  done: "text-foreground",
  partial: "text-foreground",
  todo: "text-muted-foreground/70",
};

/** Left column = gather/curate; right column = synthesize/build. Read down each
 *  column so completed work clusters top-left and the journey reads forward. */
function progressColumns(s: ConceptSnapshot): { left: Item[]; right: Item[] } {
  const scoredTone: Tone =
    s.foundCount > 0 && s.scoredCount >= s.foundCount
      ? "done"
      : s.scoredCount > 0
        ? "partial"
        : "todo";

  const left: Item[] = [
    { tone: s.hasDescription ? "done" : "todo", label: s.hasDescription ? "Concept framed" : "Concept not framed" },
    { tone: s.seedCount > 0 ? "done" : "todo", label: s.seedCount > 0 ? `${s.seedCount} seed papers` : "No seed papers" },
    { tone: s.foundCount > 0 ? "done" : "todo", label: s.foundCount > 0 ? `${s.foundCount} papers found` : "No papers found" },
    { tone: scoredTone, label: `${s.scoredCount} / ${s.foundCount} scored` },
    { tone: s.reviewedCount > 0 ? "done" : "todo", label: `${s.reviewedCount} reviewed` },
    s.untriagedCount > 0
      ? { tone: "todo", label: `Living lit: ${s.untriagedCount} new` }
      : { tone: "done", label: "Living lit: active" },
  ];

  const right: Item[] = [
    { tone: s.hasFieldDebate ? "done" : "todo", label: s.hasFieldDebate ? "Field map" : "No field map" },
    { tone: s.auditEnacted ? "done" : "todo", label: s.auditEnacted ? "Coverage audit" : "No coverage audit" },
    { tone: s.hasClaims ? "done" : "todo", label: s.hasClaims ? "Claims added" : "No claims" },
    { tone: s.hasClaimsWithEvidence ? "done" : "todo", label: s.hasClaimsWithEvidence ? "Evidence attached" : "No evidence attached" },
    { tone: s.hasArgumentAnalysis ? "done" : "todo", label: s.hasArgumentAnalysis ? "Argument tested" : "Argument not tested" },
    { tone: s.hasDraft ? "done" : "todo", label: s.hasDraft ? "Draft started" : "Draft not started" },
  ];

  return { left, right };
}

function ProgressRow({ item }: { item: Item }) {
  return (
    <div className="flex items-center gap-1.5 text-[11px] leading-tight">
      <span className={`${GLYPH_CLASS[item.tone]} w-3 shrink-0 text-center`}>{GLYPH[item.tone]}</span>
      <span className={`${LABEL_CLASS[item.tone]} truncate`}>{item.label}</span>
    </div>
  );
}

function WhereYouAre({ s }: { s: ConceptSnapshot }) {
  const { left, right } = progressColumns(s);
  return (
    <div className="rounded-lg border border-border bg-muted/30 p-3">
      <div className="mb-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        Where you are
      </div>
      <div className="grid grid-cols-2 gap-x-3">
        <div className="space-y-1">{left.map((it, i) => <ProgressRow key={i} item={it} />)}</div>
        <div className="space-y-1">{right.map((it, i) => <ProgressRow key={i} item={it} />)}</div>
      </div>
    </div>
  );
}

export function WorkflowPanel({ snapshot, recs, onAct, onOpenGuide }: Props) {
  return (
    <div className="flex flex-col gap-3">
      <WhereYouAre s={snapshot} />
      <RecommendationList recs={recs} onAct={onAct} onOpenGuide={onOpenGuide} />
    </div>
  );
}
